/**
 * MyChampionshipsSection — "Meus Campeonatos" tabs for profile page.
 * 
 * Tabs:
 *   1. Meu Campeonato   → active context (current season) via /api/sports/my-championships
 *   2. Histórico        → past championships via /api/sports/championship-history
 *   3. Campeonato Geral → link to /campeonatos (no duplication)
 * 
 * P15 Integration: Each championship card has "📅 Ver na Agenda" button
 * P17: Uses buildAgendaUrl utility for consistent deep-link generation
 * Reuses ActiveContext (sport, club_id, entity_type, entity_id) from context.
 * Backend handles all RBAC — no client-side permission filtering.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trophy, History, Globe, ChevronRight, Calendar, Medal, Loader2 } from 'lucide-react';
import { AZURE } from '../../services/api';
import { BRAND_GREEN, BRAND_GOLD, SUCRON } from '../../theme/sucronTheme';
import { buildAgendaUrl } from '../../utils/agendaNavigation'; // P17

// ─── Championship Card (shared between tabs) ──────────────────────────────

const ChampionshipCard = ({ item, onNavigate }) => {
  const navigate = useNavigate();
  const isSwimming = item.sport === 'swimming';

  // Calculate championship date range (for P15 Agenda link)
  const getChampionshipDateRange = () => {
    const year = item.season || new Date().getFullYear();
    // Approximate: championships typically run Jan-Dec of season year
    // Backend should provide actual start_date/end_date if available
    const from = `${year}-01-01`;
    const to = `${year}-12-31`;
    return { from, to };
  };

  const handleAgendaClick = (e) => {
    e.stopPropagation(); // Prevent card click navigation
    const { from, to } = getChampionshipDateRange();
    // P17: Use buildAgendaUrl utility for consistent deep-link
    const url = buildAgendaUrl({
      championship_id: item.championship_id,
      from,
      to,
      origin: 'my_championships',
      origin_id: item.championship_id,
    });
    navigate(url);
  };

  return (
    <div
      onClick={() => onNavigate(item)}
      className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:shadow-md hover:border-gray-300 transition-all cursor-pointer group"
    >
      {/* Logo / icon */}
      <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-100 bg-gray-50">
        {item.logo_url ? (
          <img src={item.logo_url} alt={item.name} className="w-10 h-10 object-contain" />
        ) : (
          <Trophy size={22} style={{ color: BRAND_GREEN }} />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-gray-900 truncate group-hover:text-[#111111] transition-colors">
          {item.name}
        </h4>
        <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
          {item.season && <span>{item.season}</span>}
          {item.country && <span>• {item.country}</span>}
        </div>
      </div>

      {/* KPIs + Actions (right side) */}
      <div className="flex items-center gap-3 text-right flex-shrink-0">
        {item.matches_played != null && (
          <div className="text-center">
            <div className="text-sm font-bold" style={{ color: SUCRON.primary }}>
              {item.matches_played}
            </div>
            <div className="text-[10px] text-gray-400">
              {isSwimming ? 'Provas' : 'Jogos'}
            </div>
          </div>
        )}
        {item.total_results != null && (
          <div className="text-center">
            <div className="text-sm font-bold" style={{ color: SUCRON.primary }}>
              {item.total_results}
            </div>
            <div className="text-[10px] text-gray-400">Resultados</div>
          </div>
        )}
        {item.podiums != null && (
          <div className="text-center">
            <div className="text-sm font-bold" style={{ color: BRAND_GOLD }}>
              {item.podiums}
            </div>
            <div className="text-[10px] text-gray-400">Pódios</div>
          </div>
        )}
        {item.goals != null && (
          <div className="text-center">
            <div className="text-sm font-bold" style={{ color: BRAND_GOLD }}>
              {item.goals}
            </div>
            <div className="text-[10px] text-gray-400">Gols</div>
          </div>
        )}
        {item.avg_rating != null && item.avg_rating > 0 && (
          <div className="text-center">
            <div className="text-sm font-bold" style={{ color: SUCRON.primary }}>
              {typeof item.avg_rating === 'number' ? item.avg_rating.toFixed(1) : item.avg_rating}
            </div>
            <div className="text-[10px] text-gray-400">Rating</div>
          </div>
        )}
        
        {/* P15: Agenda button */}
        <button
          onClick={handleAgendaClick}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all text-xs font-medium text-gray-700 whitespace-nowrap"
          title="Ver eventos deste campeonato na Agenda"
        >
          <Calendar size={14} />
          Ver na Agenda
        </button>

        <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-500" />
      </div>
    </div>
  );
};

// ─── Empty State ────────────────────────────────────────────────────────────

const EmptyState = ({ message }) => (
  <div className="text-center py-10 text-gray-400">
    <Trophy size={36} className="mx-auto mb-3 opacity-40" />
    <p className="text-sm">{message}</p>
  </div>
);

// ─── Loading Skeleton ───────────────────────────────────────────────────────

const LoadingSkeleton = () => (
  <div className="space-y-3">
    {[1, 2, 3].map(i => (
      <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
    ))}
  </div>
);

// ─── Main Section Component ─────────────────────────────────────────────────

const MyChampionshipsSection = ({ sport, clubId, entityType, entityId }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('current');  // current | history | general
  const [current, setCurrent] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingCurrent, setLoadingCurrent] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // ── Fetch current championships ──
  const fetchCurrent = useCallback(async () => {
    if (!sport || !clubId) return;
    setLoadingCurrent(true);
    try {
      const params = { sport, club_id: clubId };
      if (entityType && entityId) {
        params.entity_type = entityType;
        params.entity_id = entityId;
      }
      const { data } = await AZURE.get('/sports/my-championships', { params });
      setCurrent(data.data || []);
    } catch (err) {
      console.error('[MyChampionships] fetch current error:', err);
      setCurrent([]);
    } finally {
      setLoadingCurrent(false);
    }
  }, [sport, clubId, entityType, entityId]);

  // ── Fetch history ──
  const fetchHistory = useCallback(async () => {
    if (!sport || !clubId) return;
    setLoadingHistory(true);
    try {
      const params = { sport, club_id: clubId, limit: 10 };
      if (entityType && entityId) {
        params.entity_type = entityType;
        params.entity_id = entityId;
      }
      const { data } = await AZURE.get('/sports/championship-history', { params });
      setHistory(data.data || []);
    } catch (err) {
      console.error('[MyChampionships] fetch history error:', err);
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [sport, clubId, entityType, entityId]);

  // Fetch on mount and when context changes
  useEffect(() => { fetchCurrent(); }, [fetchCurrent]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Navigate to championship detail
  const handleNavigate = (item) => {
    if (item.sport === 'swimming') {
      navigate(`/natacao/competicao/${item.championship_id}/${item.season || new Date().getFullYear()}`);
    } else {
      navigate(`/futebol/campeonato/${item.championship_id}/${item.season || new Date().getFullYear()}`);
    }
  };

  // No context selected — prompt user
  if (!sport || !clubId) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
          <Trophy size={20} style={{ color: BRAND_GREEN }} />
          Campeonatos
        </h2>
        <p className="text-sm text-gray-500">
          Selecione um esporte e clube no <strong>Contexto Ativo</strong> para ver seus campeonatos.
        </p>
      </div>
    );
  }

  const TABS = [
    { id: 'current', label: 'Meu Campeonato', icon: Trophy },
    { id: 'history', label: 'Histórico', icon: History },
    { id: 'general', label: 'Campeonato Geral', icon: Globe },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header + Tab Nav */}
      <div className="px-6 pt-5 pb-0">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Trophy size={20} style={{ color: BRAND_GREEN }} />
          Campeonatos
        </h2>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200">
          {TABS.map(({ id, label, icon: Icon }) => {
            const isActive = tab === id;
            return (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                  isActive
                    ? 'border-[#111111] text-[#111111]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon size={15} />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6 pt-4">
        {/* ── TAB: Meu Campeonato (current season) ── */}
        {tab === 'current' && (
          loadingCurrent ? <LoadingSkeleton /> :
          current.length === 0 ? (
            <EmptyState message="Nenhum campeonato ativo encontrado para este contexto." />
          ) : (
            <div className="space-y-3">
              {current.map((item) => (
                <ChampionshipCard key={`${item.championship_id}-${item.season}`} item={item} onNavigate={handleNavigate} />
              ))}
            </div>
          )
        )}

        {/* ── TAB: Histórico ── */}
        {tab === 'history' && (
          loadingHistory ? <LoadingSkeleton /> :
          history.length === 0 ? (
            <EmptyState message="Nenhum histórico de campeonatos encontrado." />
          ) : (
            <>
              {/* Mini summary */}
              <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg text-sm">
                <div className="flex items-center gap-1.5 text-gray-600">
                  <Calendar size={14} />
                  <span><strong>{history.length}</strong> campeonatos</span>
                </div>
                {sport === 'swimming' && (() => {
                  const totalPodiums = history.reduce((sum, h) => sum + (h.podiums || 0), 0);
                  return totalPodiums > 0 ? (
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <Medal size={14} style={{ color: BRAND_GOLD }} />
                      <span><strong>{totalPodiums}</strong> pódios</span>
                    </div>
                  ) : null;
                })()}
              </div>

              <div className="space-y-3">
                {history.map((item) => (
                  <ChampionshipCard key={`${item.championship_id}-${item.season}`} item={item} onNavigate={handleNavigate} />
                ))}
              </div>
            </>
          )
        )}

        {/* ── TAB: Campeonato Geral (redirect) ── */}
        {tab === 'general' && (
          <div className="text-center py-8">
            <Globe size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="text-sm text-gray-500 mb-4">
              Explore todos os campeonatos de futebol e natação.
            </p>
            <Link
              to="/campeonatos"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-semibold transition-all hover:shadow-md"
              style={{ backgroundColor: BRAND_GREEN }}
            >
              <Trophy size={16} />
              Ir para Campeonatos
              <ChevronRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyChampionshipsSection;
